import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaMapMarkerAlt, FaStar, FaTrash, FaArrowLeft } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { getPropertyImage } from '../utils/imageHelper';
import { getFavoriteId, loadWishlistFavorites, saveWishlistFavorites } from '../utils/wishlistStorage';
import './WishlistPage.css';

// ✅ SECURITY: Get user-specific favorites key
const getFavoritesKey = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
      return `favorites_${userObj._id || userObj.id}`;
    }
  } catch (error) {
    console.error('Error getting user ID:', error);
  }
  return 'favorites'; // Fallback for non-logged-in users
};

// ✅ SECURITY: Get user-specific wishlist count key
const getWishlistCountKey = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
      return `wishlistCount_${userObj._id || userObj.id}`;
    }
  } catch (error) {
    console.error('Error getting user ID:', error);
  }
  return 'wishlistCount'; // Fallback for non-logged-in users
};

const WishlistPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = () => {
    try {
      const favoritesKey = getFavoritesKey();
      const parsed = loadWishlistFavorites(favoritesKey);
      setFavorites(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = (propertyId) => {
    try {
      const favoritesKey = getFavoritesKey();
      const wishlistCountKey = getWishlistCountKey();
      const updated = favorites.filter(fav => getFavoriteId(fav) !== propertyId);
      saveWishlistFavorites(favoritesKey, wishlistCountKey, updated);
      setFavorites(updated);
      
      // Trigger storage event to update navbar
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const handleViewProperty = (property) => {
    const propertyId = property.id || property._id;
    navigate(`/property/${propertyId}`);
  };

  if (loading) {
    return (
      <div className="wishlist-page">
        <Navbar />
        <div className="wishlist-loading">
          <div className="spinner"></div>
          <p>Loading your favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <Navbar />
      <div className="wishlist-container">
        <div className="wishlist-header">
          <button onClick={() => navigate(-1)} className="back-button">
            <FaArrowLeft />
            <span>Back</span>
          </button>
          <h1>Your Favorites</h1>
          <p className="wishlist-subtitle">
            {favorites.length === 0 
              ? "You haven't saved any properties yet" 
              : `${favorites.length} ${favorites.length === 1 ? 'property' : 'properties'} saved`}
          </p>
        </div>

        {favorites.length === 0 ? (
          <div className="empty-wishlist">
            <FaHeart className="empty-icon" />
            <h2>No favorites yet</h2>
            <p>Start exploring and save properties you love!</p>
            <button onClick={() => navigate('/search')} className="btn-explore">
              Explore Properties
            </button>
          </div>
        ) : (
          <div className="favorites-grid">
            {favorites.map((property) => {
              const propertyId = property.id || property._id;
              const imageUrl = getPropertyImage(property, 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80');
              
              return (
                <div key={propertyId} className="favorite-card">
                  <div className="favorite-image" onClick={() => handleViewProperty(property)}>
                    <img 
                      src={imageUrl} 
                      alt={property.name}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80';
                      }}
                    />
                    <button 
                      className="remove-favorite-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFavorite(propertyId);
                      }}
                      aria-label="Remove from favorites"
                    >
                      <FaTrash />
                    </button>
                    <button 
                      className="favorite-heart-btn active"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFavorite(propertyId);
                      }}
                      aria-label="Remove from favorites"
                    >
                      <FaHeart />
                    </button>
                  </div>
                  
                  <div className="favorite-content" onClick={() => handleViewProperty(property)}>
                    <h3 className="favorite-name">{property.name}</h3>
                    <div className="favorite-location">
                      <FaMapMarkerAlt />
                      <span>{property.city || property.address || 'Location not specified'}</span>
                    </div>
                    
                    {property.rating && (
                      <div className="favorite-rating">
                        <FaStar className="star-icon" />
                        <span>{property.rating}</span>
                        {property.reviews > 0 && (
                          <span className="reviews-count">({property.reviews})</span>
                        )}
                      </div>
                    )}
                    
                    {property.pricePerNight && (
                      <div className="favorite-price">
                        <span className="price-amount">₹{property.pricePerNight.toLocaleString()}</span>
                        <span className="price-label">per night</span>
                      </div>
                    )}
                    
                    <button 
                      className="btn-view-details"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewProperty(property);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;








